export type TileMatchTile = {
  id: string;
  imageAssetKey: string;
  questionId?: string;
  x: number;
  y: number;
  z: number;
  isBlocked: boolean;
};

/** Matches 56px tiles on a 28px grid (see TileMatchQuizPlayer BOARD_CELL_PX). */
export const TILE_MATCH_TILE_GRID_SIZE = 2;

export type TileMatchBoardBounds = {
  minX: number;
  minY: number;
  spanX: number;
  spanY: number;
};

/** Tight bounding box of placed tiles (grid units) for compact board layout. */
export function getTileMatchBoardBounds(
  board: Pick<TileMatchTile, 'x' | 'y'>[]
): TileMatchBoardBounds {
  if (board.length === 0) {
    return { minX: 0, minY: 0, spanX: 4, spanY: 3 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = 0;
  let maxY = 0;
  for (const t of board) {
    minX = Math.min(minX, t.x);
    minY = Math.min(minY, t.y);
    maxX = Math.max(maxX, t.x + TILE_MATCH_TILE_GRID_SIZE);
    maxY = Math.max(maxY, t.y + TILE_MATCH_TILE_GRID_SIZE);
  }
  return {
    minX,
    minY,
    spanX: Math.max(1, maxX - minX),
    spanY: Math.max(1, maxY - minY),
  };
}

export function rectsOverlap(
  a: Pick<TileMatchTile, 'x' | 'y'>,
  b: Pick<TileMatchTile, 'x' | 'y'>,
  tileSize = TILE_MATCH_TILE_GRID_SIZE
): boolean {
  return (
    a.x < b.x + tileSize &&
    a.x + tileSize > b.x &&
    a.y < b.y + tileSize &&
    a.y + tileSize > b.y
  );
}

export function isTileBlocked(tile: TileMatchTile, board: TileMatchTile[]): boolean {
  return board.some(
    (other) =>
      other.id !== tile.id && other.z > tile.z && rectsOverlap(tile, other)
  );
}

export function withBlockingFlags(board: TileMatchTile[]): TileMatchTile[] {
  return board.map((t) => ({ ...t, isBlocked: isTileBlocked(t, board) }));
}

export function normalizeTileMatchKey(imageAssetKey: string): string {
  return imageAssetKey.trim().toLowerCase();
}

export function selectionBarHasTrio(bar: Pick<TileMatchTile, 'imageAssetKey'>[]): boolean {
  const counts = new Map<string, number>();
  for (const t of bar) {
    const k = normalizeTileMatchKey(t.imageAssetKey);
    if (!k) continue;
    counts.set(k, (counts.get(k) ?? 0) + 1);
    if ((counts.get(k) ?? 0) >= 3) return true;
  }
  return false;
}

/**
 * Mirrors {@code TileMatchEngine.TryClearTrio}: first image key (bar order) with count ≥ 3,
 * remove the first three tiles of that key in the bar.
 */
export function tryClearSelectionBarTrio(bar: TileMatchTile[]): {
  bar: TileMatchTile[];
  cleared: boolean;
} {
  const seenKeys = new Set<string>();
  for (const t of bar) {
    const k = normalizeTileMatchKey(t.imageAssetKey);
    if (!k || seenKeys.has(k)) continue;
    seenKeys.add(k);
    const matching = bar.filter((x) => normalizeTileMatchKey(x.imageAssetKey) === k);
    if (matching.length >= 3) {
      const removeIds = new Set(matching.slice(0, 3).map((x) => x.id));
      return { bar: bar.filter((x) => !removeIds.has(x.id)), cleared: true };
    }
  }
  return { bar, cleared: false };
}

export type ApplyTileMatchPickResult =
  | {
      ok: true;
      board: TileMatchTile[];
      selectionBar: TileMatchTile[];
      trioCleared: boolean;
      wouldGameOver: boolean;
    }
  | { ok: false; reason: 'not_found' | 'blocked' };

/**
 * Optimistic preview of {@code TileMatchEngine.TryPick} (append to bar, clear trio, recompute blocking).
 * Score / round win / session end remain server-only.
 */
export function applyTileMatchPickLocal(
  board: TileMatchTile[],
  selectionBar: TileMatchTile[],
  tileId: string,
  barCapacity = 7
): ApplyTileMatchPickResult {
  const picked = board.find((t) => t.id === tileId);
  if (!picked) return { ok: false, reason: 'not_found' };
  if (isTileBlocked(picked, board)) return { ok: false, reason: 'blocked' };

  const boardWithout = board.filter((t) => t.id !== tileId);
  let bar = [...selectionBar, { ...picked }];
  const newBoard = withBlockingFlags(boardWithout);

  const { bar: barAfterTrio, cleared: trioCleared } = tryClearSelectionBarTrio(bar);
  bar = barAfterTrio;

  const wouldGameOver =
    (!trioCleared && bar.length >= barCapacity) || isRunDeadlocked(newBoard, bar);

  return {
    ok: true,
    board: newBoard,
    selectionBar: bar,
    trioCleared,
    wouldGameOver,
  };
}

export function hasPickableTile(board: TileMatchTile[]): boolean {
  return board.some((t) => !isTileBlocked(t, board));
}

export function isRoundCleared(
  board: TileMatchTile[],
  bar: Pick<TileMatchTile, 'imageAssetKey'>[]
): boolean {
  return board.length === 0 && bar.length === 0;
}

/** No pickable tiles on board and bar cannot form a trio (excludes round-cleared state). */
export function isRunDeadlocked(
  board: TileMatchTile[],
  bar: Pick<TileMatchTile, 'imageAssetKey'>[]
): boolean {
  if (isRoundCleared(board, bar)) return false;
  return !selectionBarHasTrio(bar) && !hasPickableTile(board);
}
